import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { InputSwitch } from "primereact/inputswitch";
import { MultiSelect } from "primereact/multiselect";
import { TreeTable } from "primereact/treetable";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router";
import { useEditableFlag } from "../../hooks/useEditableFlag";
import { checkLoading, SystemLoading } from "../../store/loadingStore";
import { handleSuccessMessage, MessageContext } from "../../store/messageStore";
import { queryRoleFunctionByRoleSeq, updateRoleFunctionIdList } from "../../utility/service/personal-service";
import { checkDpSuccess } from "../../utility/service/tool-service";
import { AcctContext } from "../../utility/useReduce/userNameReduce";

const RoleFunctionMaintain = () => {
  // ==================== Hooks 和狀態管理 ====================
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { itemObj } = useOutletContext();
  const [, dispatch] = useContext(MessageContext); // 訊息視窗
  const [, loading] = useContext(SystemLoading);
  const [state, dispatchUser] = useContext(AcctContext); //訊息視窗

  // 編輯權限控制：檢查當前用戶是否有 F202 功能的編輯權限
  const isEditable = useEditableFlag("F202");

  // 狀態管理
  const [treeData, setTreeData] = useState([]); // 樹狀結構數據
  const [expandedNodeKeys, setExpandedNodeKeys] = useState({}); // 展開狀態
  const [selectedNodeKeys, setSelectedNodeKeys] = useState({});
  const [permissionKeys, setPermissionKeys] = useState({}); // 編輯權限設定
  const [tabList, setTabList] = useState({}); // 使用的頁籤
  const [tabPermissionsKeys, setTabPermissionsKeys] = useState({}); // 頁籤設定

  useEffect(() => {
    if (JSON.stringify(itemObj) !== "{}") {
      axios_queryRoleFunctionByRoleSeq(itemObj?.roleSeq);
    } else {
      navigate("/management/rolePermission");
    }
  }, [itemObj, navigate]);

  // 查詢角色功能權限
  const axios_queryRoleFunctionByRoleSeq = useCallback(
    (roleSeq) => {
      checkLoading(loading);
      queryRoleFunctionByRoleSeq({ roleSeq })
        .then((res) => {
          if (checkDpSuccess(res)) {
            const functionListData = res?.data?.RespBody?.functionList;

            const parentKeys = [];
            const selectedKeys = [];
            const editableKeys = [];
            const leafTabPermissions = {};
            const selectLeafTab = {};

            const convertToTreeTableFormat = (node, parentKey = "", childIndex = 0, siblingsLength = 1) => {
              const key = parentKey ? `${parentKey}-${childIndex}` : `${childIndex}`;

              if (node.children && node.children.length > 0) {
                parentKeys.push(key);
              }

              if (node.isCheck === "Y") {
                selectedKeys.push(key);
              }

              if (node.editableFlag === "Y") {
                editableKeys.push(key);
              }

              if ((!node.children || node.children.length === 0) && node.availableTabs) {
                leafTabPermissions[key] = node.availableTabs.split("|");
              }

              if ((!node.children || node.children.length === 0) && node.tabPermissions) {
                selectLeafTab[key] = node.tabPermissions.split("|");
              }

              return {
                key,
                data: {
                  functionId: node.functionId,
                  name: node.name,
                  disabled: node.disabled,
                  functionLevel: node.functionLevel,
                  isCheck: node.isCheck,
                  editableFlag: node.editableFlag,
                  availableTabs: node.availableTabs,
                  tabPermissions: node.tabPermissions,
                  isLastSibling: childIndex === siblingsLength - 1,
                },
                children: node.children
                  ? node.children.map((child, index) =>
                      convertToTreeTableFormat(child, key, index, node.children.length)
                    )
                  : [],
              };
            };

            const tree = functionListData.map((node, index) =>
              convertToTreeTableFormat(node, "", index, functionListData.length)
            );

            setTreeData(tree);

            // 展開所有有子節點的父節點
            setExpandedNodeKeys(Object.fromEntries(parentKeys.map((k) => [k, true])));

            // 設定所有勾選的節點為選中狀態
            setSelectedNodeKeys(
              Object.fromEntries(selectedKeys.map((k) => [k, { checked: true, partialChecked: false, }]))
            );

            // 設定是否可以編輯欄位
            setPermissionKeys(Object.fromEntries(editableKeys.map((k) => [k, true])));

            // 頁籤權限設定
            setTabList(leafTabPermissions);

            // 目前選中的頁籤
            setTabPermissionsKeys(selectLeafTab);
          }
        })
        .finally(() => checkLoading(loading, false));
    },
    [loading]
  );

  // 處理勾選的值
  const handleSelectionChange = (e) => {
    const updateNodeEditableFlag = (nodeList, targetKey, flag) => {
      return nodeList.map((node) => {
        if (node.key === targetKey) {
          return { ...node, editableFlag: flag };
        } else if (node.children) {
          return {
            ...node,
            children: updateNodeEditableFlag(node.children, targetKey, flag),
          };
        }
        return node;
      });
    };

    const newSelected = e.value;
    const prevKeys = Object.keys(selectedNodeKeys);
    const newKeys = Object.keys(newSelected);

    // 新勾選的節點
    const addedKeys = newKeys.filter((k) => !prevKeys.includes(k));
    // 被取消勾選的節點
    const removedKeys = prevKeys.filter((k) => !newKeys.includes(k));

    let updatedNodes = [...treeData];

    // 勾選的節點 → editableFlag = 'Y'
    addedKeys.forEach((key) => {
      updatedNodes = updateNodeEditableFlag(updatedNodes, key, "Y");
      setPermissionKeys((prev) => ({
        ...prev,
        [key]: true,
      }));
      setTabPermissionsKeys((prev) => ({
        ...prev,
        [key]: tabList[key],
      }));
    });

    // 取消勾選的節點 → editableFlag = 'N'
    removedKeys.forEach((key) => {
      updatedNodes = updateNodeEditableFlag(updatedNodes, key, "N");
      setPermissionKeys((prev) => ({
        ...prev,
        [key]: false,
      }));
      setTabPermissionsKeys((prev) => ({
        ...prev,
        [key]: [],
      }));
    });

    setTreeData(updatedNodes);
    setSelectedNodeKeys(newSelected);
  };

  // 處理可編輯欄位
  const handleEditableChange = (key, value) => {
    setPermissionKeys((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // 處理選單欄位
  const handleTabPermissionsChange = (key, value) => {
    setTabPermissionsKeys((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // 更新角色功能權限
  const axios_updateRoleFunctionIdList = () => {
    const findNodeByKey = (tree, key) => {
      for (let node of tree) {
        if (node.key === key) return node;
        if (node.children) {
          const found = findNodeByKey(node.children, key);
          if (found) return found;
        }
      }
      return null;
    };

    checkLoading(loading);

    const functionIdList = Object.keys(selectedNodeKeys).map((key) => {
      const node = findNodeByKey(treeData, key);
      return node?.data.functionId ?? key;
    });

    const permissionMappings = Object.keys(selectedNodeKeys).map((key) => {
      const node = findNodeByKey(treeData, key);
      return {
        functionId: node?.data.functionId ?? key,
        editableFlag: permissionKeys[key] ? "Y" : "N",
        tabPermissions: tabPermissionsKeys[key]?.join("|") ?? null,
      };
    });

    const reqBody = {
      roleSeq: itemObj?.roleSeq,
      functionIdList: functionIdList,
      permissionMappings: permissionMappings,
    };
    updateRoleFunctionIdList(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
          navigate("/management/rolePermission", {
            state: { roleSeq: itemObj?.roleSeq },
          });
        }
      })
      .finally(() => {
        dispatchUser({
          type: "REFLASH_FLAG",
          payload: { reflashFlag: !state.reflashFlag },
        });
        checkLoading(loading, false);
      });
  };

  // 將頁籤名稱轉換為多語系顯示
  const getTabDisplayName = (tabList) => {
    const tabNameMap = {
      // F10103 盤管管理
      inventory_information: t("inventory_information"),
      organizational_boundary: t("organizational_boundary"),
      emission_source_identification: t("emission_source_identification"),
      importanceIdentification: t("importanceIdentification"),
      activity_data: t("activity_data"),
      emissions_calculation: t("Emissions_calculation"),
      inventory: t("button.inventory"), // 清冊
      report: t("button.report"),

      // F10503 碳足跡盤查
      raw_material: t("stage.rawMaterial"),
      manufacturing: t("stage.manufacturing"),
      distribution: t("stage.distribution"),
      use_phase: t("stage.use"),
      service_phase: t("stage.service"),
      disposal_phase: t("stage.disposal"),
      footprint_calculation: t("stage.footCalc"),
      data_quality: t("stage.dataQualtity"),
      sensitivity_analysis: t("stage.sensitivityAnalysis"),

      inventory_info: t("inventory_information"),
      manufacturing_info: t("manufacturingInfo"),
      emission_source: t("emission_source"),
      energy_usage: t("energyUsage"),
      process_info: t("process"),
      precursor_info: t("precursor"),
      product_overview: t("productOverview"),
    };

    return tabList.map((tabName) => {
      if (tabNameMap[tabName]) {
        return {
          value: tabName,
          label: tabNameMap[tabName],
        };
      }
    });
  };

  return (
    <>
      {/* 返回列表按鈕 */}
      <div className="align-items-start">
        <Button
          rounded
          text
          onClick={() => {
            navigate("/management/rolePermission");
          }}
        >
          <FontAwesomeIcon icon="fa-solid fa-arrow-left-long" />
          {t("button.return_to_list")}
        </Button>
      </div>
      <div className="p-card">
        <div className="p-card-body">
          {/* 角色基本資料顯示 */}
          <div className="grid">
            <div className="col-12">
              <label className="font-semibold mr-3">{t("role_symbol")}</label>
              <span>{itemObj?.symbol}</span>
            </div>
            <div className="col-12">
              <label className="font-semibold mr-3"> {t("role_name")}</label>
              <span>{itemObj?.name}</span>
            </div>
          </div>
          <hr />

          {/* 功能權限樹狀表格 */}
          <TreeTable
            className={`white-data-table hover-highlight tree-lines ${!isEditable ? "disabled-table" : ""} `}
            value={treeData}
            expandedKeys={expandedNodeKeys}
            onToggle={(e) => setExpandedNodeKeys(e.value)}
            selectionMode="checkbox"
            selectionKeys={selectedNodeKeys}
            onSelectionChange={handleSelectionChange}
            emptyMessage={t("no_rec")}
            tableStyle={{ minWidth: "55rem" }}
            rowClassName={(rowData) => {
              return `level-${rowData.data.functionLevel} ${rowData.data.isLastSibling ? "last-sibling" : ""} ${
                rowData.data.disabled ? "p-disabled" : ""
              }`;
            }}
          >
            {/* 功能ID欄位（帶展開器） */}
            <Column field="functionId" header={t("fun_id")} className="font-bold" style={{ width: "12rem" }} expander />
            {/* 功能名稱欄位 */}
            <Column field="name" header={t("fun_name")} style={{ width: "13rem" }} className="font-bold" />
            {/* 編輯權限欄位 */}
            <Column
              field="editableFlag"
              header={t("editFlag")}
              style={{ width: "8rem" }}
              body={(rowData) => {
                return (
                  <InputSwitch
                    checked={permissionKeys[rowData.key]}
                    disabled={!isEditable || !selectedNodeKeys[rowData.key]}
                    onChange={(e) => handleEditableChange(rowData.key, e.value)}
                  />
                );
              }}
            />
            {/* 頁籤權限欄位 */}
            <Column
              field="tabPermissions"
              header={t("tabPermissions")}
              style={{ width: "40rem" }}
              body={(rowData) => {
                if (!tabList[rowData.key]) {
                  return null;
                }
                return (
                  <MultiSelect
                    value={tabPermissionsKeys[rowData.key]}
                    options={getTabDisplayName(tabList[rowData.key] || [])}
                    optionLabel="label"
                    optionValue="value"
                    disabled={!isEditable || !selectedNodeKeys[rowData.key]}
                    onChange={(e) => {
                      handleTabPermissionsChange(rowData.key, e.value);
                    }}
                    placeholder={t("tabPermissions")}
                    className="w-full"
                    display="chip"
                    showClear
                    filter
                    selectedItemsLabel={t("tabsSelected")}
                  />
                );
              }}
            />
          </TreeTable>
          {/* 操作按鈕區域 */}
          <div className="grid mt-2">
            <div className="col-12 gap-2 flex-all-center">
              <Button
                disabled={!isEditable}
                onClick={() => {
                  axios_updateRoleFunctionIdList();
                }}
                label={t("button.update")}
                size="small"
              />
              <Button
                outlined
                size="small"
                label={t("button.cancel")}
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/management/rolePermission", {
                    state: { roleSeq: itemObj?.roleSeq },
                  });
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RoleFunctionMaintain;
